import { clearStoredToken, getValidToken } from "@/lib/auth-token"
import { API_BASE_URL } from "@/lib/config"

export interface ApiResponse<T> {
  success: boolean
  message: string
  data: T
  details?: unknown
}

/** Error thrown for any failed admin API call; keeps the HTTP status for callers. */
export class ApiError extends Error {
  readonly status: number
  readonly details?: unknown

  constructor(message: string, status: number, details?: unknown) {
    super(message)
    this.name = "ApiError"
    this.status = status
    this.details = details
  }
}

function handleUnauthorized() {
  clearStoredToken()
  // Avoid a redirect loop when the login page itself gets a 401.
  if (window.location.pathname !== "/") {
    window.location.replace("/?session=expired")
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
  const normalizedEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`
  const url = `${API_BASE_URL}/api/admin${normalizedEndpoint}`

  const headers = new Headers(options.headers)
  if (!(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json")
  }

  const token = getValidToken()
  if (token) headers.set("Authorization", `Bearer ${token}`)

  let response: Response
  try {
    response = await fetch(url, { ...options, headers })
  } catch {
    throw new ApiError("Cannot reach the API server. Check your connection and try again.", 0)
  }

  if (response.status === 401 || response.status === 403) {
    handleUnauthorized()
    throw new ApiError("Your session has expired. Please sign in again.", response.status)
  }

  const contentType = response.headers.get("content-type")
  if (!contentType?.includes("application/json")) {
    throw new ApiError(`Unexpected response from server (${response.status} ${response.statusText})`, response.status)
  }

  const data = (await response.json()) as ApiResponse<T>
  if (!response.ok || !data.success) {
    throw new ApiError(data?.message || `Request failed with status ${response.status}`, response.status, data?.details)
  }

  return data
}

export const apiClient = {
  request,
  get: <T>(endpoint: string) => request<T>(endpoint, { method: "GET" }),
  post: <T>(endpoint: string, data: unknown) =>
    request<T>(endpoint, { method: "POST", body: JSON.stringify(data) }),
  upload: <T>(endpoint: string, data: FormData) => request<T>(endpoint, { method: "POST", body: data }),
  put: <T>(endpoint: string, data: unknown) =>
    request<T>(endpoint, { method: "PUT", body: JSON.stringify(data) }),
  delete: <T = unknown>(endpoint: string) => request<T>(endpoint, { method: "DELETE" }),
}

export function getErrorMessage(error: unknown, fallback = "Something went wrong") {
  if (error instanceof Error && error.message) return error.message
  return fallback
}
